import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { timeLookupsAPI, timeProfilesAPI, timeReportsAPI } from '../services/api';
import { TimeEntry, TimeProfile, TimeProjectLookup } from '../types';
import { formatDate, formatHours, todayIso } from '../utils';
import TableLoadingRow from './TableLoadingRow';

interface MemberTimeSummary {
  profile: TimeProfile;
  totalMinutes: number;
  trackedDates: Set<string>;
  firstTrackedDate?: string;
  lastTrackedDate?: string;
  monthlyMinutes: Record<string, number>;
}

const REPORT_START_DATE = '2000-01-01';
const PAGE_SIZE = 500;

const getEntryDate = (entry: TimeEntry) => entry.workDate.slice(0, 10);
const getEntryMonth = (entry: TimeEntry) => getEntryDate(entry).slice(0, 7);

const formatMonth = (month: string) => {
  const [year, monthNumber] = month.split('-');
  return `${monthNumber}.${year}`;
};

const buildMemberSummaries = (profiles: TimeProfile[], dayOffEntries: TimeEntry[]) => {
  const summaries = new Map<number, MemberTimeSummary>();

  profiles.forEach(profile => {
    summaries.set(profile.id, {
      profile,
      totalMinutes: 0,
      trackedDates: new Set<string>(),
      monthlyMinutes: {},
    });
  });

  dayOffEntries.forEach(entry => {
    const existing = summaries.get(entry.timeProfileId);
    const profile = existing?.profile || {
      id: entry.timeProfileId,
      displayName: entry.timeProfileName || 'Unknown member',
      email: '',
      role: 'User',
      isActive: true,
      ignoreForWarnings: false,
      expectedWeeklyHours: 0,
      createdDate: '',
    };
    const summary: MemberTimeSummary = existing || {
      profile,
      totalMinutes: 0,
      trackedDates: new Set<string>(),
      monthlyMinutes: {},
    };
    const entryDate = getEntryDate(entry);
    const entryMonth = getEntryMonth(entry);

    summary.totalMinutes += entry.durationMinutes;
    summary.trackedDates.add(entryDate);
    summary.monthlyMinutes[entryMonth] = (summary.monthlyMinutes[entryMonth] || 0) + entry.durationMinutes;
    summary.firstTrackedDate = !summary.firstTrackedDate || entryDate < summary.firstTrackedDate ? entryDate : summary.firstTrackedDate;
    summary.lastTrackedDate = !summary.lastTrackedDate || entryDate > summary.lastTrackedDate ? entryDate : summary.lastTrackedDate;
    summaries.set(entry.timeProfileId, summary);
  });

  return Array.from(summaries.values())
    .sort((a, b) => b.totalMinutes - a.totalMinutes || a.profile.displayName.localeCompare(b.profile.displayName));
};

const MemberTimeSummaryPage: React.FC = () => {
  const [profiles, setProfiles] = useState<TimeProfile[]>([]);
  const [projects, setProjects] = useState<TimeProjectLookup[]>([]);
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const loadAllEntries = async () => {
      const allEntries: TimeEntry[] = [];
      let skip = 0;
      let totalCount = Number.POSITIVE_INFINITY;

      while (skip < totalCount) {
        const page = await timeReportsAPI.getDetailed({
          startDate: REPORT_START_DATE,
          endDate: todayIso(),
          skip,
          take: PAGE_SIZE,
        });
        allEntries.push(...page.items);
        totalCount = page.totalCount;
        skip += page.take || PAGE_SIZE;

        if (page.items.length === 0) {
          break;
        }
      }

      return allEntries;
    };

    const loadData = async () => {
      setLoading(true);
      try {
        const [profileData, projectData, entryData] = await Promise.all([
          timeProfilesAPI.getProfiles(true),
          timeLookupsAPI.getProjects(),
          loadAllEntries(),
        ]);

        if (!isMounted) {
          return;
        }

        setProfiles(profileData);
        setProjects(projectData);
        setEntries(entryData);
      } catch {
        if (isMounted) {
          toast.error('Member time summary could not be loaded');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, []);

  const dayOffProjectIds = useMemo(
    () => new Set(projects.filter(project => project.isDayOffProject).map(project => project.id)),
    [projects]
  );
  const dayOffEntries = useMemo(
    () => entries.filter(entry => typeof entry.projectId === 'number' && dayOffProjectIds.has(entry.projectId)),
    [dayOffProjectIds, entries]
  );
  const months = useMemo(
    () => Array.from(new Set(dayOffEntries.map(getEntryMonth))).sort(),
    [dayOffEntries]
  );
  const summaries = useMemo(
    () => buildMemberSummaries(profiles, dayOffEntries),
    [profiles, dayOffEntries]
  );
  const totalMinutes = useMemo(
    () => summaries.reduce((sum, summary) => sum + summary.totalMinutes, 0),
    [summaries]
  );
  const totalTrackedDays = useMemo(
    () => summaries.reduce((sum, summary) => sum + summary.trackedDates.size, 0),
    [summaries]
  );

  return (
    <div className="fade-in">
      <div className="page-header">
        <div>
          <h1>Member time off summary</h1>
          <p>Total day-off project time by member and month</p>
        </div>
      </div>

      <div className="stat-grid member-time-stat-grid">
        <div className="stat-card">
          <span>Total members</span>
          <strong>{summaries.length}</strong>
        </div>
        <div className="stat-card">
          <span>Total time off</span>
          <strong>{formatHours(totalMinutes / 60)}</strong>
        </div>
        <div className="stat-card">
          <span>Total day-off days</span>
          <strong>{totalTrackedDays}</strong>
        </div>
      </div>

      <section className="card">
        <div className="card-body">
          <div className="section-heading">
            <div>
              <h2 className="section-title">Time off by member</h2>
              <p className="section-subtitle">Shows each member's total time off from projects marked as day-off projects, number of day-off work dates, first/last day off, and monthly time-off totals.</p>
            </div>
          </div>
          <div className="table-responsive member-time-table-wrap">
            <table className="table member-time-table">
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Total time off</th>
                  <th>Day-off days</th>
                  <th>First day off</th>
                  <th>Last day off</th>
                  {months.map(month => <th key={month}>{formatMonth(month)}</th>)}
                </tr>
              </thead>
              <tbody>
                {loading && <TableLoadingRow colSpan={5 + Math.max(months.length, 1)} />}
                {!loading && summaries.map(summary => (
                  <tr key={summary.profile.id}>
                    <td data-label="Member">
                      <div className="member-time-name">{summary.profile.displayName}</div>
                      <div className="member-time-email">{summary.profile.email || 'No email'}</div>
                    </td>
                    <td data-label="Total time off">{formatHours(summary.totalMinutes / 60)}</td>
                    <td data-label="Day-off days">{summary.trackedDates.size}</td>
                    <td data-label="First day off">{summary.firstTrackedDate ? formatDate(summary.firstTrackedDate) : '—'}</td>
                    <td data-label="Last day off">{summary.lastTrackedDate ? formatDate(summary.lastTrackedDate) : '—'}</td>
                    {months.map(month => (
                      <td data-label={formatMonth(month)} key={month}>
                        {summary.monthlyMinutes[month] ? formatHours(summary.monthlyMinutes[month] / 60) : '—'}
                      </td>
                    ))}
                  </tr>
                ))}
                {!loading && summaries.length === 0 && (
                  <tr>
                    <td colSpan={5 + Math.max(months.length, 1)}>
                      <div className="empty-state compact">No day-off project time entries found</div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MemberTimeSummaryPage;
